import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Card, Badge, EmptyState, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Plus, FileSpreadsheet, Paperclip, Eye } from "lucide-react";

const CATEGORIES = ["Salaries", "Travel", "Office Rent", "Utilities", "Marketing", "Manufacturing", "Logistics", "Software", "Other"];

export default function Expenses() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [centers, setCenters] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split("T")[0], amount: "", cost_center: "", category: "", description: "", payment_method: "cash", notes: "", attachment_id: null });
  const [uploading, setUploading] = useState(false);

  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "expense_receipt");
      const token = localStorage.getItem("access_token");
      const r = await fetch(`${API_BASE}/uploads?category=expense_receipt`, {
        method: "POST", body: fd, credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      setForm(f => ({ ...f, attachment_id: data.id, attachment_name: data.original_filename }));
    } catch (e) {
      alert("Upload error: " + e.message);
    } finally { setUploading(false); }
  };

  const load = async () => {
    const [e, c] = await Promise.all([api.get("/expenses"), api.get("/cost-centers")]);
    setList(e.data); setCenters(c.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/expenses", { ...form, amount: Number(form.amount) });
    setOpen(false);
    setForm({ date: new Date().toISOString().split("T")[0], amount: "", cost_center: "", category: "", description: "", payment_method: "cash", notes: "", attachment_id: null });
    await load();
  };

  // Group by cost center
  const byCenter = {};
  list.forEach(e => { byCenter[e.cost_center] = (byCenter[e.cost_center] || 0) + e.amount; });
  const total = list.reduce((s, e) => s + e.amount, 0);

  return (
    <div data-testid="expenses-page">
      <PageHeader
        title={t("expenses")}
        subtitle="Cost center management & expense tracking"
        actions={
          <>
            <Btn variant="secondary" onClick={() => window.open(`${API_BASE}/export/expenses/excel`)}><FileSpreadsheet className="w-4 h-4"/>Excel</Btn>
            <Btn onClick={() => setOpen(true)} data-testid="add-expense-btn"><Plus className="w-4 h-4"/>{t("add")}</Btn>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5"><div className="overline">Total</div><div className="metric-value mt-1">{fmtMoney(total)} EGP</div><div className="text-xs text-slate-500 mt-1">{list.length} entries</div></Card>
        <Card className="p-5">
          <div className="overline mb-2">By Cost Center (top 5)</div>
          <div className="space-y-1">
            {Object.entries(byCenter).sort((a,b) => b[1]-a[1]).slice(0,5).map(([cc, amt]) => (
              <div key={cc} className="flex justify-between text-sm">
                <span className="font-mono text-xs">{cc}</span>
                <span className="font-bold">{fmtMoney(amt)}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <div className="overline mb-2">Cost Centers</div>
          <div className="grid grid-cols-3 gap-1 text-xs">
            {centers.map(c => <span key={c.code} className="font-mono text-slate-600">{c.code}</span>)}
          </div>
        </Card>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 px-3 text-start">Date</th>
                <th className="py-2 px-3 text-start">Cost Center</th>
                <th className="py-2 px-3 text-start">Category</th>
                <th className="py-2 px-3 text-start">Description</th>
                <th className="py-2 px-3 text-start">Method</th>
                <th className="py-2 px-3 text-end">Amount</th>
                <th className="py-2 px-3 text-center">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {list.map(e => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 px-3 text-xs">{e.date}</td>
                  <td className="py-2 px-3"><Badge color="blue">{e.cost_center}</Badge></td>
                  <td className="py-2 px-3 text-slate-700">{e.category}</td>
                  <td className="py-2 px-3 text-slate-600 text-xs">{e.description || "-"}</td>
                  <td className="py-2 px-3 text-xs"><Badge color="slate">{e.payment_method}</Badge></td>
                  <td className="py-2 px-3 text-end font-bold text-red-700">{fmtMoney(e.amount)}</td>
                  <td className="py-2 px-3 text-center">
                    {e.attachment_id ? (
                      <a href={`${API_BASE}/uploads/${e.attachment_id}/download?auth=${localStorage.getItem("access_token")}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:bg-blue-50 p-1 rounded inline-flex">
                        <Eye className="w-4 h-4"/>
                      </a>
                    ) : <span className="text-slate-300">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <EmptyState message={t("noData")}/>}
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Expense">
        <form onSubmit={submit} className="space-y-3" data-testid="expense-form">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Date *" type="date" required value={form.date} onChange={(e) => setForm({...form, date: e.target.value})}/>
            <Input label="Amount (EGP) *" type="number" required value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})}/>
            <Select label="Cost Center *" required value={form.cost_center} onChange={(e) => setForm({...form, cost_center: e.target.value})}>
              <option value="">--</option>
              {centers.map(c => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
            </Select>
            <Select label="Category *" required value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
              <option value="">--</option>{CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </Select>
            <Select label="Payment Method" value={form.payment_method} onChange={(e) => setForm({...form, payment_method: e.target.value})}>
              <option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="check">Check</option>
            </Select>
          </div>
          <Textarea label="Description" rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
          <Input label="Notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
          <div>
            <label className="block text-sm font-medium text-slate-900 dark:text-slate-200 mb-1 inline-flex items-center gap-1">
              <Paperclip className="w-4 h-4"/> Receipt (image or PDF)
            </label>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => uploadReceipt(e.target.files[0])} data-testid="expense-receipt"
              className="block w-full text-sm text-slate-700 file:me-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 file:font-semibold hover:file:bg-blue-100"/>
            {uploading && <div className="text-xs text-slate-500 mt-1">Uploading...</div>}
            {form.attachment_id && <div className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1"><Paperclip className="w-3 h-3"/>Attached: {form.attachment_name}</div>}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Btn variant="ghost" type="button" onClick={() => setOpen(false)}>{t("cancel")}</Btn>
            <Btn type="submit">{t("save")}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
